import { useCallback, useEffect, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { supabase, readableError } from '../lib/supabase'
import { Shell } from '../components/Shell'
import { Spinner, Check } from '../components/ui'

/**
 * Confirms account deletion via token from email link.
 *
 * The token was created by send-delete-account-email, valid for 7 days.
 * Calling confirm_account_deletion() soft-deletes the account and all songs,
 * logs out, and shows the result.
 */
export default function DeleteConfirm() {
  const nav = useNavigate()
  const [params] = useSearchParams()
  const [step, setStep] = useState('confirming') // confirming | done | error
  const [err, setErr] = useState(null)

  const token = params.get('token')

  const confirm = useCallback(async () => {
    if (!token) {
      setErr('No deletion token in URL. Check your email for the confirmation link.')
      setStep('error')
      return
    }

    try {
      const { error } = await supabase.rpc('confirm_account_deletion', { p_token: token })
      if (error) throw error

      // Account deleted successfully — sign out and show done screen
      await supabase.auth.signOut().catch(() => {})
      setStep('done')
    } catch (e) {
      setErr(readableError(e))
      setStep('error')
    }
  }, [token])

  useEffect(() => {
    confirm()
  }, [confirm])

  if (step === 'confirming') {
    return (
      <Shell>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100%', gap: 16, paddingBottom: 80 }}>
          <Spinner />
          <p className="small">Confirming your deletion…</p>
        </div>
      </Shell>
    )
  }

  if (step === 'done') {
    return (
      <Shell>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100%', gap: 16, textAlign: 'center', paddingBottom: 80 }}>
          <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'var(--ok)', display: 'grid', placeItems: 'center' }}>
            <Check size={28} w={2.4} color="white" />
          </div>
          <h2 className="q" style={{ marginBottom: 0 }}>Account deleted.</h2>
          <p className="sub">
            Your account and songs are being removed. You can sign back in within 90 days to restore everything.
          </p>
          <button className="btn btn-primary" style={{ marginTop: 12 }} onClick={() => nav('/')}>
            Back to login
          </button>
        </div>
      </Shell>
    )
  }

  return (
    <Shell>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100%', gap: 16, textAlign: 'center', paddingBottom: 80 }}>
        <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'var(--danger)', display: 'grid', placeItems: 'center', opacity: 0.2 }} />
        <h2 className="q" style={{ marginBottom: 0 }}>Couldn't delete your account.</h2>
        <p className="sub">{err || 'Something went wrong. Try again or contact support.'}</p>
        <button className="btn btn-quiet" onClick={() => nav('/')}>
          Back to login
        </button>
      </div>
    </Shell>
  )
}
